<script lang="ts">
  import Layout from "$app/Layouts/Plan.svelte"
  import { onDestroy, onMount } from "svelte"
  import { Pulse } from "svelte-loading-spinners"
  import {
    convertObjectToUrl,
    toast,
    wait,
    xhr,
    resumeOnboarding,
    cancelOnboarding
  } from "$app/utils"
  import ByDepartment from "./ByDepartment.svelte"
  import ByHierarchy from "./ByHierarchy.svelte"
  import ByAssignee from "./ByAssignee.svelte"
  import NewGoal from "./NewGoal.svelte"
  import NewTask from "./NewTask.svelte"
  import GoalDetail from "./GoalDetail.svelte"
  import Filters from "./Filters/Index.svelte"
  import MyProgress from "./MyProgress.svelte"
  import Onboarding from "$app/Pages/Plans/OnePageView/Onboarding.svelte"
  import GuidedSetup from "./GuidedSetup/Index.svelte"
  import Modal from "$app/components/Modal.svelte"
  import StatusBar from "./StatusBar.svelte"
  import { buildUserList } from "$app/utils/user_methods"
  import { ClipboardCheck, Lock } from "lucide-svelte"
  import { appStore } from "$app/store.svelte"

  interface Props {
    plan: PlanType
    departments: DepartmentType[]
    users: UserType[]
    dates: { quarter: number; year: number }
    update_goals?: boolean
    params: any
    current_user: UserType
    onboarding: any
    last_meeting_date: string | Date
    today: string | Date
    root_goal: GoalType
    end_of_quarter: string
    end_of_year: string
  }

  let props = $props()

  let {
    plan,
    departments,
    users,
    dates,
    update_goals = false,
    params,
    last_meeting_date,
    today,
    root_goal,
    end_of_quarter,
    end_of_year
  }: Props = props

  today = new Date(`${today}T00:00:00`)
  last_meeting_date = new Date(`${last_meeting_date}T00:00:00`)

  appStore.today = today
  appStore.dates = dates
  appStore.last_meeting_date = last_meeting_date
  appStore.root_goal = root_goal
  appStore.end_of_quarter = new Date(`${end_of_quarter}T00:00:00`)
  appStore.end_of_year = new Date(`${end_of_year}T00:00:00`)
  appStore.page = params.page || "department"
  appStore.departments = departments
  appStore.editable = update_goals
  const userList = buildUserList(users)

  appStore.assignees = [
    ...departments.map((d) => {
      return { ...d, type: "Department" }
    }),
    ...userList.map((u) => {
      return { ...u, type: "User" }
    })
  ] as AssigneeType[]

  const allAssignees = [
    ...departments.map((d) => {
      return { ...d, type: "Department" }
    }),
    ...userList.map((u) => {
      return { ...u, type: "User" }
    })
  ] as AssigneeType[]

  const hostname = location.hostname.substring(
    0,
    location.hostname.indexOf(".")
  )

  let filterHeading = $state("")
  let openNewGoalModal = $state(false)
  let openTaskModal = $state(false)
  let filterComp: Filters = $state()
  let goalData: any = $state({ goals: [], tasks: [] })
  let isLoading = $state(true)
  let currentGoal: GoalType = $state()

  let newGoalComp: NewGoal = $state()

  let openGoalDetail = $state(false)

  type numStringObjType = { [key: number]: string }

  type respType = {
    goals: GoalType[]
    tasks: TaskType[]
    assignees: AssigneeType[]
    departments: DepartmentType[]
    goal_progresses_dates: numStringObjType
    task_progresses_dates: numStringObjType
  }

  type itemDateType = {
    id: number
    created_at: string
  }

  function setProgressDates(
    items: itemDateType[],
    dates: numStringObjType
  ): any[] {
    return items.map((item) => {
      let pd: Date | null = null

      pd = new Date(`${dates[item.id]}T00:00:00`)
      if ("Invalid Date" === pd.toString()) pd = null

      return {
        ...item,
        progress_date: pd,
        created_at: new Date(item.created_at)
      }
    })
  }

  function setGoals(data: {
    goals: GoalType[]
    goal_progresses_dates: { [key: number]: string }
  }): void {
    appStore.goals = setProgressDates(
      data.goals as itemDateType[],
      data.goal_progresses_dates
    ) as GoalType[]
  }

  async function setTasks(data: {
    tasks: TaskType[]
    task_progresses_dates: numStringObjType
  }): Promise<void> {
    appStore.tasks = setProgressDates(
      data.tasks as itemDateType[],
      data.task_progresses_dates
    ) as TaskType[]
  }

  function setAssignees(): void {
    if (appStore.goalParams.department || appStore.goalParams.user) {
      const deptAssignees = Array.isArray(appStore.goalParams.department)
        ? allAssignees.filter(
            (a) =>
              a.type === "Department" &&
              appStore.goalParams.department.includes(a.id)
          )
        : []
      const userAssignees = Array.isArray(appStore.goalParams.user)
        ? allAssignees.filter(
            (a) => a.type === "User" && appStore.goalParams.user.includes(a.id)
          )
        : []

      appStore.assignees = [...deptAssignees, ...userAssignees]
    } else {
      appStore.assignees = [...allAssignees]
    }
  }

  function setData(data: respType) {
    if (appStore.page === "hierarchy") {
      appStore.goals = data.goals
      appStore.tasks = data.tasks
      expandGoals = true
    } else {
      if (appStore.page !== "assignee") {
        appStore.departments = data.departments
      }
      setGoals(data)
      setTasks(data)
      setAssignees()
    }
  }

  async function getData(filters: any): Promise<void> {
    if (appStore.page === "my_progress") {
      getMyProgressData()
      return
    }
    filters = filters ? filters : filterComp.getData()

    let data: respType
    isLoading = true

    try {
      const url = convertObjectToUrl({ page: appStore.page, ...filters })
      window.history.pushState(
        { page: appStore.page, ...filters },
        "Plan Goals",
        `?${url}`
      )
      const resp = await xhr.get(
        `/plans/${plan.id}/goals/by_${appStore.page}.json?${url}`
      )

      appStore.goalParams = filters
      filterHeading = resp.data.filter_heading
      setData(resp.data as respType)
    } catch (e) {
      console.error(e)
      toast("There was an error loading the department goals", "error")
    } finally {
      isLoading = false
    }
  }

  async function getMyProgressData(): Promise<void> {
    isLoading = true

    const dqs = convertObjectToUrl({
      expected_at: filterComp.getData()["expected_at"]
    })

    try {
      const resp = await xhr.get(
        `/plans/${plan.id}/goals/my_progress.json?${dqs}`
      )

      goalData = resp.data
      window.history.pushState(
        { page: appStore.page },
        "Plan Goals",
        `?page=${appStore.page}&${dqs}`
      )
    } catch (e) {
      console.error(e)
      toast("There was an error loading the My Goals", "error")
    } finally {
      isLoading = false
    }
  }

  async function toggleTabs(tab: string): Promise<void> {
    appStore.page = tab
    isLoading = true
    await wait(30)

    setTimeout(() => {
      if (appStore.page === "my_progress") {
        getMyProgressData()
      } else {
        getData(null)
      }
      expandGoals = false
    })
  }

  function addGoal(event) {
    currentGoal = {
      ...event.detail,
      progress_date: null,
      tasks: []
    } as GoalType
    openNewGoalModal = true
  }

  let expandGoals = $state(false)
  let expandGoalsStr = $state(null)

  const expandFn = (d: HTMLElement) => {
    if (expandGoalsStr !== d.dataset["expanded"]) {
      d.click()
    }
  }

  function getExpandButtons(): NodeListOf<Element> {
    return document.querySelectorAll("[data-name=goal-expander]")
  }

  async function expandHierachy(): Promise<void> {
    getExpandButtons().forEach(expandFn)
    await wait(100)
    getExpandButtons().forEach(expandFn)
    await wait(100)
    getExpandButtons().forEach(expandFn)
  }

  const openGoalDetailEvent = (e: CustomEvent) => {
    currentGoal = e.detail.goal as GoalType
    openGoalDetail = true
  }

  let freePlan: boolean = $state(false)

  onMount(() => {
    isLoading = true

    freePlan = appStore.subscription.subscription_type == "free"

    if (!appStore.onboarding.completed_onboarding) {
      return
    }

    if (appStore.page === "my_progress") {
      getMyProgressData()
    } else {
      getData(null)
    }

    // @ts-ignore
    document.addEventListener("show-goal-detail", openGoalDetailEvent)
  })

  onDestroy(() => {
    // @ts-ignore
    document.removeEventListener("show-goal-detail", openGoalDetailEvent)
  })
</script>

<Layout {...props}>
  <div class="max-w-7xl mx-auto sm:px-6 lg:px-8 pb-8 py-6">
    {#if appStore.onboarding.completed_onboarding}
      <div class="max-w-3xl mx-auto px-1 md:px-4 sm:px-6 lg:max-w-7xl lg:px-8">
        <div class="pb-6">
          <Filters
            {dates}
            page={appStore.page}
            onsearch={(data) => getData(data)}
            bind:this={filterComp}
          />
        </div>

        {#if appStore.page !== "my_progress"}
          <div class="md:grid goal-statuses justify-between">
            <div class="flex justify-start mb-6 md:mb-0">
              <h1 class="text-3xl">Goals</h1>
              {#if !isLoading && filterHeading}
                <div class="text-sm px-2 flex items-center text-gray-700">
                  {filterHeading}
                </div>
              {/if}

              {#if update_goals || appStore.page === "assignee"}
                <button
                  class="px-5 ml-4 bg-white text-blue-800 rounded-full shadow font-base py-1"
                  data-name="add-goal"
                  onclick={() => {
                    const gType = { department: "QuarterlyGoal", assignee: "" }[
                      appStore.page
                    ]
                    if (appStore.page === "assignee") {
                      openTaskModal = true
                    } else {
                      addGoal({ detail: { type: gType } })
                    }
                  }}
                >
                  {#if appStore.page === "department"}
                    + New Quarterly Goal
                  {:else if appStore.page === "assignee"}
                    + New Task
                  {:else if appStore.page === "hierarchy"}
                    + New Goal
                  {/if}
                </button>
              {/if}
            </div>
            {#if !isLoading}
              <StatusBar />
            {/if}
          </div>
        {:else}
          <div class="md:grid goal-statuses justify-between">
            <div class="flex justify-start mb-6 md:mb-0">
              <h1 class="text-3xl">My Progress</h1>
            </div>
          </div>
        {/if}

        <div class="sm:hidden my-4">
          <label for="tabs" class="sr-only">Select a tab</label>
          <select
            id="tabs"
            name="tabs"
            bind:value={appStore.page}
            class="block w-full focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-md"
            onchange={(e) => toggleTabs(e.target.value)}
          >
            <option value="department">By Department</option>
            <option value="assignee">By Assignee</option>
            <option value="hierarchy">By Hierarchy</option>
            {#if hostname !== "teams"}
              <option value="my_progress"
                >My Progress
                {#if freePlan}
                  <Lock class="size-5 text-gray-300 ml-1" />
                {/if}
              </option>
            {/if}
          </select>
        </div>
        <div class="mt-4 bg-white shadow min-h-96">
          <div>
            <div class="overflow-visible pb-6">
              <div class="bg-white hidden sm:block">
                <div class="border-b border-gray-200 flex justify-between">
                  <nav class="-mb-px flex tabs px-4" aria-label="Tabs">
                    <a
                      href="/plans/{plan.id}/goals?page=department"
                      onclick={(e) => {
                        e.preventDefault()
                        toggleTabs("department")
                      }}
                      class:active={appStore.page === "department"}
                      class="tab"
                    >
                      <div class="px-4 whitespace-nowrap">By Department</div>
                    </a>
                    <a
                      id="by-assignee"
                      href="/plans/{plan.id}/goals?page=assignee"
                      onclick={(e) => {
                        e.preventDefault()
                        appStore.page = "assignee"
                        setTimeout(() => {
                          toggleTabs("assignee")
                        })
                      }}
                      class:active={appStore.page === "assignee"}
                      class="tab"
                    >
                      <div class="px-4 whitespace-nowrap">By Assignee</div>
                    </a>
                    <a
                      href="/plans/{plan.id}/goals?page=hierarchy"
                      onclick={(e) => {
                        e.preventDefault()
                        toggleTabs("hierarchy")
                      }}
                      class:active={appStore.page === "hierarchy"}
                      class="tab whitespace-nowrap px-4"
                    >
                      <div class="px-4 whitespace-nowrap">By Hierarchy</div>
                    </a>
                    {#if hostname !== "teams"}
                      <a
                        href="/plans/{plan.id}/goals?page=my_progress"
                        onclick={(e) => {
                          e.preventDefault()
                          appStore.page = "my_progress"
                          setTimeout(() => {
                            toggleTabs("my_progress")
                          })
                        }}
                        class:active={appStore.page === "my_progress"}
                        class="tab whitespace-nowrap px-4"
                      >
                        <div class="px-4 whitespace-nowrap flex items-center">
                          {#if freePlan}
                            <Lock class="size-4 text-gray-300 mr-1" />
                          {/if}
                          My Progress
                        </div>
                      </a>
                    {/if}
                  </nav>
                  {#if ["department", "hierarchy"].includes(appStore.page)}
                    <div class="px-3 py-2 flex items-center">
                      <span class="isolate inline-flex rounded-md shadow-sm">
                        <button
                          type="button"
                          data-name="goals-collapse"
                          onclick={() => (expandGoals = false)}
                          class="relative inline-flex items-center rounded-l-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 focus:z-10 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          Collapse All
                        </button>
                        <button
                          type="button"
                          data-name="goals-expand"
                          onclick={() => (expandGoals = true)}
                          class="relative -ml-px inline-flex items-center rounded-r-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 focus:z-10 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          Expand All
                        </button>
                      </span>
                    </div>
                  {/if}
                </div>
              </div>

              {#if isLoading}
                <div class="py-12 flex justify-center w-full">
                  <Pulse size="240" color="#cdcdcd" unit="px" duration="1s" />
                </div>
              {:else}
                <div class="px-1 sm:px-4">
                  {#if appStore.page === "department"}
                    <ByDepartment expanded={expandGoals} />
                  {:else if appStore.page === "assignee"}
                    <ByAssignee />
                  {:else if appStore.page === "hierarchy"}
                    <ByHierarchy {expandGoals} />
                  {:else if appStore.page === "my_progress"}
                    <MyProgress {goalData} />
                  {/if}
                </div>
              {/if}
            </div>
          </div>
        </div>
      </div>
    {:else}
      <Onboarding
        onresume={() => resumeOnboarding(plan.id)}
        oncancel={() => cancelOnboarding(plan.id)}
      />

      <div class="bg-white">
        <div class="px-4 py-6 sm:px-6 relative">
          <h2
            class="text-2xl text-center text-gray-800 flex justify-center items-center gap-1"
          >
            <ClipboardCheck class="size-6 text-gray-700" />
            Goals
          </h2>

          <GuidedSetup rootGoalId={root_goal.id} />
        </div>
      </div>
    {/if}
  </div>

  <!--Add goal-->
  <Modal
    bind:open={openNewGoalModal}
    closeConfirm={true}
    onclose={() => {
      if (newGoalComp.changesCount() > 1) {
        if (
          confirm(
            "Are you sure you want to close the modal? You will lose all your changes!"
          )
        ) {
          openNewGoalModal = false
        }
      } else {
        openNewGoalModal = false
      }
    }}
  >
    <NewGoal
      oncreate={() => (openNewGoalModal = false)}
      bind:this={newGoalComp}
      goal={currentGoal}
    />
  </Modal>

  <!--Goal detail-->
  <Modal bind:open={openGoalDetail} large={true}>
    <GoalDetail bind:goal={currentGoal} />
  </Modal>

  <!--Add new task-->
  <Modal bind:open={openTaskModal}>
    <NewTask
      assignee_id={null}
      parent_goal_id={null}
      end_of_quarter={appStore.end_of_quarter}
      oncreate={(t) => (openTaskModal = false)}
    />
  </Modal>

  <style>
    .goal-statuses {
      grid-template-columns: 30rem 25rem;
    }
  </style>
</Layout>
